import {booleanAttribute, Directive, EventEmitter, Input, Output, Type} from "@angular/core";

import * as Models from "app/services/proxy/model/models";

import {Lookup, TypeDispatcher, UtilsService} from "framework/services/utils.service";
import {BaseComponent} from "framework/ui/components";
import {ControlOption} from "framework/ui/control-option";
import {Memoizer} from "framework/utils/memoizers";

@Directive()
export abstract class DynamicParametersBaseComponent<T extends DynamicParameterExtended> extends BaseComponent
{
    protected readonly ParameterInputType = ParameterInputType;

    @Input({transform: booleanAttribute}) withWrapper: boolean;
    @Input({transform: booleanAttribute}) showHiddenAsSecrets: boolean;
    @Input() label: string = "Policy Parameters";
    @Input() validationErrors: Models.ValidationResults;

    @Output() valueChange     = new EventEmitter<string>();
    @Output() formValidChange = new EventEmitter<boolean>();

    protected params: T[];

    ngOnInit()
    {
        this.params = this.buildParams();
        this.valueChangeEmit();
    }

    public valueChangeEmit()
    {
        this.valueChange.emit(DynamicParameterExtended.generateParametersString(this.params));
        this.formValidChange.emit(this.isValid());
    }

    public isValid(): boolean
    {
        // Validity is derived directly from each parameter's schema rather than an NgForm:
        // the fields live inside this component's own view (no ng-content), so an ancestor
        // form's @ContentChildren(NgModel) can never see them, and reading form.valid inside
        // (ngModelChange) lags a keystroke. Schema.isValid is synchronous and always current.
        return (this.params || []).every((param) => !param.show() || param.schema.isValid(param.value, param.required()));
    }

    public getErrors(param: DynamicParameterExtended): string[]
    {
        if (this.validationErrors?.entries.length)
        {
            const errors: string[] = [];
            for (let error of this.validationErrors.entries)
            {
                const fields = error.field.split(".");
                if (fields.shift() !== param.name()) continue;
                const prefix = fields.length ? `${fields.join(".")} - ` : "";
                errors.push(`${prefix}${error.reason}`);
            }
            return errors;
        }

        return null;
    }

    protected updateBool(param: DynamicParameterExtended,
                         value: boolean)
    {
        param.formValue = JSON.stringify(!!value);
        this.valueChangeEmit();
    }

    protected abstract buildParams(): T[];
}

export class DynamicParameterExtended
{
    public readonly schema: ParameterSchemaExtended<any, any, any>;

    protected m_value: any;
    get value(): any
    {
        return this.m_value;
    }

    get formValue(): any
    {
        return this.schema.getFormValue(this.value);
    }

    set formValue(formValue: any)
    {
        this.m_value = this.schema.getValue(formValue);
    }

    constructor(public readonly parameter: Models.NamedParameter,
                private readonly m_initialValue: any)
    {
        this.schema        = ParameterSchemaExtended.newInstance(parameter.schema);
        this.m_value       = this.m_initialValue;
        const defaultValue = parameter.schema.default_value;
        if ((this.m_value === undefined || this.m_value === null) && defaultValue)
        {
            try
            {
                this.m_value = JSON.parse(defaultValue);
            }
            catch
            {
                this.m_value = defaultValue;
            }
        }
    }

    name(): string
    {
        return this.parameter.name;
    }

    label(): string
    {
        return this.parameter.name;
    }

    description(): string
    {
        return this.parameter.schema.description;
    }

    required(): boolean
    {
        return true;
    }

    show(): boolean
    {
        return true;
    }

    public static generateParametersString(paramExts: DynamicParameterExtended[]): string
    {
        const value: Lookup<any> = {};
        for (let param of paramExts || [])
        {
            value[param.name()] = param.value;
        }

        return JSON.stringify(value);
    }
}

export abstract class ParameterSchemaExtended<T extends Models.ParameterBase, V, F>
{
    private static readonly s_dispatcher = new TypeDispatcher<Models.ParameterBase, ParameterSchemaExtended<Models.ParameterBase, any, any>>();

    public static register(type: Type<Models.ParameterBase>)
    {
        return ParameterSchemaExtended.s_dispatcher.register(type);
    }

    public static newInstance(schema: Models.ParameterBase): ParameterSchemaExtended<any, any, any>
    {
        return ParameterSchemaExtended.s_dispatcher.create(schema);
    }

    constructor(public schema: T) {}

    abstract getInputType(): ParameterInputType;

    abstract getFormValue(value: V): F;

    abstract getValue(formValue: F): V;

    @Memoizer
    getAllowedValues(): ControlOption<string>[]
    {
        return [];
    }

    isValid(value: V,
            required: boolean): boolean
    {
        return true;
    }

    getPattern(): string
    {
        return "";
    }

    getMinLength(): number
    {
        return undefined;
    }

    getMaxLength(): number
    {
        return undefined;
    }

    getMinValue(): number
    {
        return undefined;
    }

    getMaxValue(): number
    {
        return undefined;
    }
}

@ParameterSchemaExtended.register(Models.ParameterStringType)
class StringSchemaExtended extends ParameterSchemaExtended<Models.ParameterStringType, string, string>
{
    public getInputType(): ParameterInputType
    {
        const constraints = this.schema.constraints || [];
        for (let constraint of constraints)
        {
            if (constraint instanceof Models.ParameterConstraintForAllowedValues)
            {
                return ParameterInputType.Dropdown;
            }
        }
        return ParameterInputType.String;
    }

    @Memoizer
    public getAllowedValues(): ControlOption<string>[]
    {
        const constraints = this.schema.constraints || [];
        for (let constraint of constraints)
        {
            if (constraint instanceof Models.ParameterConstraintForAllowedValues)
            {
                return constraint.allowed_values.map((c) => new ControlOption<string>(c.value, c.display_name));
            }
        }

        return [];
    }

    public getPattern(): string
    {
        const constraints = this.schema.constraints || [];
        for (let constraint of constraints)
        {
            if (constraint instanceof Models.ParameterConstraintForRegexPattern)
            {
                return constraint.allowed_pattern;
            }
        }

        return "";
    }

    // todo: combine

    getMinLength(): number
    {
        const constraints = this.schema.constraints || [];
        for (let constraint of constraints)
        {
            if (constraint instanceof Models.ParameterConstraintForLength)
            {
                return constraint.min_length;
            }
        }

        return undefined;
    }

    getMaxLength(): number
    {
        const constraints = this.schema.constraints || [];
        for (let constraint of constraints)
        {
            if (constraint instanceof Models.ParameterConstraintForLength)
            {
                return constraint.max_length;
            }
        }

        return undefined;
    }

    public getFormValue(value: string): string
    {
        return value;
    }

    public getValue(formValue: string): string
    {
        return formValue;
    }

    public isValid(value: string,
                   required: boolean): boolean
    {
        required ||= !!this.getMinLength();
        if (required && !value) return false;

        const allowedValues = this.getAllowedValues();
        if (allowedValues.length) return allowedValues.some((option) => option.id === value);

        const regex = this.getPattern();
        if (regex && !value.match(regex)) return false;

        const minLength = this.getMinLength();
        if (UtilsService.isNumber(minLength) && value.length < minLength) return false;

        const maxLength = this.getMaxLength();
        if (UtilsService.isNumber(maxLength) && value.length > maxLength) return false;

        return true;
    }
}

@ParameterSchemaExtended.register(Models.ParameterNumberType)
class NumberSchemaExtended extends ParameterSchemaExtended<Models.ParameterNumberType, number, string>
{
    private m_minValue: number;
    private m_maxValue: number;

    public getMinValue(): number
    {
        return this.m_minValue;
    }

    public getMaxValue(): number
    {
        return this.m_maxValue;
    }

    constructor(schema: Models.ParameterNumberType)
    {
        super(schema);

        for (let constraint of this.schema.constraints || [])
        {
            if (constraint instanceof Models.ParameterConstraintForNumericRange)
            {
                this.m_minValue = constraint.min;
                this.m_maxValue = constraint.max;
            }
        }
    }

    public getInputType(): ParameterInputType
    {
        return ParameterInputType.Number;
    }

    public getFormValue(value: number): string
    {
        return UtilsService.isNumber(value) ? "" + value : "";
    }

    public getValue(formValue: string): number
    {
        // Angular's number input emits null/"" when cleared; coercing that with `+formValue`
        // yields 0, silently turning "no value" into a real zero for optional params. Return
        // undefined instead so generateParametersString drops the key from the payload.
        if (formValue === null || formValue === undefined || ("" + formValue).trim() === "")
        {
            return undefined;
        }
        return +formValue;
    }

    public isValid(value: number,
                   required: boolean): boolean
    {
        if (required && !UtilsService.isNumber(value)) return false;
        if (UtilsService.isNumber(this.m_minValue) && value < this.m_minValue) return false;
        if (UtilsService.isNumber(this.m_maxValue) && value > this.m_maxValue) return false;

        return true;
    }
}

@ParameterSchemaExtended.register(Models.ParameterBooleanType)
class BooleanSchemaExtended extends ParameterSchemaExtended<Models.ParameterBooleanType, boolean, string>
{
    public getInputType(): ParameterInputType
    {
        return ParameterInputType.Checkbox;
    }

    public getFormValue(value: boolean): string
    {
        return "" + !!value;
    }

    public getValue(formValue: string): boolean
    {
        return formValue?.toLowerCase() == "true";
    }
}

@ParameterSchemaExtended.register(Models.ParameterObject)
class ObjectSchemaExtended extends ParameterSchemaExtended<Models.ParameterObject, Lookup<any>, string>
{
    private m_formValue: string;

    public getInputType(): ParameterInputType
    {
        return ParameterInputType.Textarea;
    }

    public getFormValue(value: Lookup<any>): string
    {
        if (!this.m_formValue)
        {
            this.m_formValue = JSON.stringify(value, null, 2);
        }
        return this.m_formValue;
    }

    public getValue(formValue: string): Lookup<any>
    {
        this.m_formValue = formValue;
        if (!formValue) return {};
        try
        {
            return JSON.parse(formValue);
        }
        catch
        {
            // Return something not an object so it shows as invalid
            return <any>"{";
        }
    }

    public isValid(value: Lookup<any>): boolean
    {
        // getValue returns the string sentinel "{" when the JSON failed to parse; a valid
        // value is always an object, so a string here means the textarea holds malformed JSON.
        return typeof value !== "string";
    }
}

@ParameterSchemaExtended.register(Models.ParameterJsonBlob)
class JsonBlobSchemaExtended extends ParameterSchemaExtended<Models.ParameterJsonBlob, any, string>
{
    private m_formValue: string;

    public getInputType(): ParameterInputType
    {
        return ParameterInputType.Textarea;
    }

    public getFormValue(value: Lookup<any>): string
    {
        if (!this.m_formValue)
        {
            this.m_formValue = JSON.stringify(value, null, 2);
        }
        return this.m_formValue;
    }

    public getValue(formValue: string): Lookup<any>
    {
        this.m_formValue = formValue;
        if (!formValue) return {};
        try
        {
            return JSON.parse(formValue);
        }
        catch
        {
            // Return something not an object so it shows as invalid
            return <any>"{";
        }
    }
}

@ParameterSchemaExtended.register(Models.ParameterMap)
class MapSchemaExtended extends ParameterSchemaExtended<Models.ParameterMap, Lookup<any>, string>
{
    private m_formValue: string;

    public getInputType(): ParameterInputType
    {
        return ParameterInputType.Textarea;
    }

    public getFormValue(value: Lookup<any>): string
    {
        if (!this.m_formValue)
        {
            this.m_formValue = JSON.stringify(value, null, 2);
        }
        return this.m_formValue;
    }

    public getValue(formValue: string): Lookup<any>
    {
        this.m_formValue = formValue;
        if (!formValue) return {};
        try
        {
            return JSON.parse(formValue);
        }
        catch
        {
            // Return something not an object so it shows as invalid
            return <any>"{";
        }
    }

    public isValid(value: Lookup<any>): boolean
    {
        // getValue returns the string sentinel "{" when the JSON failed to parse; a valid
        // value is always an object, so a string here means the textarea holds malformed JSON.
        return typeof value !== "string";
    }
}

@ParameterSchemaExtended.register(Models.ParameterArray)
class ArraySchemaExtended extends ParameterSchemaExtended<Models.ParameterArray, any[], string[] | string>
{
    private m_formValue: string;

    public getInputType(): ParameterInputType
    {
        if (this.schema.values_schema instanceof Models.ParameterStringType)
        {
            return this.getAllowedValues().length ? ParameterInputType.MultiSelect : ParameterInputType.StringSet;
        }

        return ParameterInputType.Textarea;
    }

    @Memoizer
    public getAllowedValues(): ControlOption<string>[]
    {
        const valuesSchema = this.schema.values_schema;
        const constraints  = valuesSchema instanceof Models.ParameterStringType ? valuesSchema.constraints || [] : [];
        for (let constraint of constraints)
        {
            if (constraint instanceof Models.ParameterConstraintForAllowedValues)
            {
                return constraint.allowed_values.map((c) => new ControlOption<string>(c.value, c.display_name || c.value));
            }
        }

        return [];
    }

    public getFormValue(value: any[]): string[] | string
    {
        const inputType = this.getInputType();
        if (inputType == ParameterInputType.StringSet || inputType == ParameterInputType.MultiSelect)
        {
            return value;
        }

        if (!this.m_formValue)
        {
            this.m_formValue = JSON.stringify(value, null, 2);
        }

        return this.m_formValue;
    }

    public getValue(formValue: string[] | string): any[]
    {
        if (typeof formValue == "string")
        {
            this.m_formValue = formValue;
            if (!formValue) return [];
            try
            {
                return JSON.parse(formValue);
            }
            catch
            {
                // Return something not an object so it shows as invalid
                return <any>"{";
            }
        }
        else
        {
            return formValue;
        }
    }

    public isValid(value: any[],
                   required: boolean): boolean
    {
        // A string here is the "{" sentinel from a failed JSON.parse (textarea mode); a valid
        // value is always an array. Also treat an empty array as invalid when the param is required.
        if (typeof value === "string") return false;
        if (required && !value?.length) return false;

        return true;
    }
}


export enum ParameterInputType
{
    Checkbox    = "checkbox",
    Dropdown    = "dropdown",
    MultiSelect = "multiSelect",
    Number      = "number",
    String      = "string",
    StringSet   = "stringSet",
    Textarea    = "textarea"
}
